import useSWR from 'swr';
import { ExternalLink, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getReviewsStats, type ReviewProvider } from '@/api/reviews';
import { useZoneFeatures } from '@/hooks/useZoneFeatures';

const PROVIDER_LABEL: Record<ReviewProvider, string> = {
  yandex: 'Яндекс Карты', '2gis': '2ГИС', linza: 'QR-форма',
};

/** «Другое» в отчёте недели — новые отзывы за неделю, ссылками (Alexey 16.09).
 *  Визиты у недели уже есть своей карточкой, поэтому здесь только отзывы. */
export function WeekOtherCard({ zoneId, from, to }: { zoneId: string; from: string; to: string }) {
  const { features } = useZoneFeatures(zoneId);
  const on = !!features?.reviews?.enabled;
  const { data } = useSWR(
    on && zoneId ? ['week-reviews', zoneId, from, to] : null,
    async () => (await getReviewsStats(zoneId, from, to)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  if (!on || !data || data.period.count === 0) return null;
  return (
    <Card>
      <CardHeader className="pt-4 pb-2">
        <CardTitle className="text-sm font-semibold">Другое</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm">
          Новые отзывы: <b>{data.period.count}</b>
          {data.period.avg_rating !== null && <>, средняя оценка <b>{data.period.avg_rating.toFixed(1)}</b></>}
        </p>
        <ul className="space-y-1.5">
          {data.reviews.slice(0, 8).map((r) => (
            <li key={r.id} className="flex flex-wrap items-baseline gap-x-2 text-sm">
              {r.rating !== null && (
                <span className="inline-flex items-center gap-0.5 font-medium">
                  {r.rating}<Star className="size-3 fill-amber-400 text-amber-400" />
                </span>
              )}
              <span className="text-xs text-muted-foreground">{PROVIDER_LABEL[r.provider]}</span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{r.text}</span>
              {r.url && (
                <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-xs text-primary hover:underline">
                  открыть <ExternalLink className="size-3" />
                </a>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
